import { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2, Save, Plus, Trash2, Settings, FileText, Users,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function LMSAdmin() {
  const [activeTab, setActiveTab] = useState("leave-types");
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveGroups, setLeaveGroups] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Bulk grant
  const [bulkType, setBulkType] = useState("");
  const [bulkDays, setBulkDays] = useState("");
  const [bulkDept, setBulkDept] = useState("");

  useEffect(() => {
    Promise.all([
      fetchLeaveTypes(),
      fetchLeaveGroups(),
      fetchWorkflows(),
      fetchSettings(),
    ]).finally(() => setLoading(false));
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      const res = await axios.get(`${API}/lms/leave-types`, {
        params: { include_disabled: true },
        headers: getHeaders(), withCredentials: true,
      });
      setLeaveTypes(res.data.leave_types || []);
    } catch {}
  };

  const fetchLeaveGroups = async () => {
    try {
      const res = await axios.get(`${API}/lms/leave-groups`, {
        headers: getHeaders(), withCredentials: true,
      });
      setLeaveGroups(res.data.leave_groups || []);
    } catch {}
  };

  const fetchWorkflows = async () => {
    try {
      const res = await axios.get(`${API}/lms/approval-workflows`, {
        headers: getHeaders(), withCredentials: true,
      });
      setWorkflows(res.data.approval_workflows || []);
    } catch {}
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/lms/admin/settings`, {
        headers: getHeaders(), withCredentials: true,
      });
      setSettings(res.data.settings || {});
    } catch {}
  };

  const handleCreateType = async () => {
    const code = prompt("Leave type code (e.g., ANNUAL):");
    if (!code) return;
    const name = prompt("Leave type name:");
    if (!name) return;
    const days = prompt("Default entitlement days (e.g., 25):");
    const colour = prompt("Hex colour (e.g., #3b82f6):") || "#6366f1";
    try {
      await axios.post(`${API}/lms/leave-types`, {
        code, name, colour,
        default_entitlement_days: parseFloat(days) || 0,
        is_paid: true,
      }, { headers: getHeaders(), withCredentials: true });
      toast.success("Leave type created");
      fetchLeaveTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    }
  };

  const handleBulkGrant = async () => {
    if (!bulkType || !bulkDays) {
      toast.error("Select leave type and enter days");
      return;
    }
    try {
      const res = await axios.post(`${API}/lms/admin/grant-bulk`, {
        leave_type_id: bulkType, days: parseFloat(bulkDays),
        department: bulkDept || undefined,
      }, { headers: getHeaders(), withCredentials: true });
      toast.success(`Granted to ${res.data.granted_to} employees`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk grant failed");
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await axios.put(`${API}/lms/admin/settings`, settings, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
    </div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-lg font-semibold text-slate-800 mb-4">Leave Admin Console</h2>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-none border border-slate-200 bg-white mb-4">
          <TabsTrigger value="leave-types" className="text-xs rounded-none data-[state=active]:bg-emerald-50">
            <FileText className="w-3.5 h-3.5 mr-1" /> Leave Types
          </TabsTrigger>
          <TabsTrigger value="workflows" className="text-xs rounded-none data-[state=active]:bg-emerald-50">
            <Users className="w-3.5 h-3.5 mr-1" /> Workflows
          </TabsTrigger>
          <TabsTrigger value="rules" className="text-xs rounded-none data-[state=active]:bg-emerald-50">
            <Settings className="w-3.5 h-3.5 mr-1" /> Rules
          </TabsTrigger>
          <TabsTrigger value="bulk" className="text-xs rounded-none data-[state=active]:bg-emerald-50">
            <Plus className="w-3.5 h-3.5 mr-1" /> Bulk Grant
          </TabsTrigger>
        </TabsList>

        {/* Leave Types */}
        <TabsContent value="leave-types">
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Configured Leave Types</h3>
              <Button onClick={handleCreateType} size="sm"
                className="h-7 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500">
                <Plus className="w-3 h-3 mr-1" /> Add Type
              </Button>
            </div>
            <div className="space-y-2">
              {leaveTypes.map((lt) => (
                <div key={lt.id} className="flex items-center justify-between p-3 border border-slate-100 hover:bg-app">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: lt.colour }} />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{lt.name}</p>
                      <p className="text-[10px] text-slate-400">
                        {lt.code} &middot;
                        {lt.default_entitlement_days ? `${lt.default_entitlement_days} days/yr` : "No default"}
                        {lt.requires_certificate && " &middot; Certificate required"}
                        {lt.auto_approval_limit_days && ` &middot; Auto-approve ≤${lt.auto_approval_limit_days}d`}
                      </p>
                    </div>
                  </div>
                  <Badge className={`text-[9px] px-1.5 py-0 border-none ${
                    lt.is_disabled ? "bg-slate-100 text-slate-400" : "bg-emerald-100 text-emerald-700"
                  }`}>
                    {lt.is_disabled ? "Disabled" : "Active"}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Workflows */}
        <TabsContent value="workflows">
          <div className="bg-white border border-slate-200 p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Approval Workflows
            </h3>
            {workflows.length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">No workflows configured. Create leave types from the Leave Types tab to configure workflows.</p>
            ) : (
              <div className="space-y-2">
                {workflows.map((wf) => (
                  <div key={wf.id} className="flex items-center justify-between p-3 border border-slate-100">
                    <div>
                      <p className="text-xs font-medium text-slate-800">{wf.name}</p>
                      <p className="text-[10px] text-slate-400">
                        Stage {wf.stage_order} &middot; Role: {wf.approver_role || "Any"} &middot;
                        {wf.escalation_days ? `${wf.escalation_days}d escalation` : "No escalation"}
                      </p>
                    </div>
                    <Badge className="text-[9px] px-1.5 py-0 bg-slate-100 text-slate-600 border-none">
                      Stage {wf.stage_order}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Rules */}
        <TabsContent value="rules">
          <div className="bg-white border border-slate-200 p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Automatic Rules
            </h3>
            {settings && (
              <div className="space-y-3">
                {[
                  { key: "auto_reject_insufficient_credits", label: "Auto-reject if insufficient credits" },
                  { key: "auto_recreate_as_unpaid", label: "Auto-recreate as UNPAID if leave type rejected" },
                  { key: "partial_approval_split", label: "Partial approval rule: Split application when partial balance" },
                  { key: "prevent_cancellation_after_approval", label: "Prevent cancellation after approval" },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-3 p-2 hover:bg-app cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings[key] || false}
                      onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-xs text-slate-700">{label}</span>
                  </label>
                ))}
                <div className="pt-2">
                  <label className="text-xs text-slate-600 block mb-1">
                    Auto-approve escalation (days):
                  </label>
                  <Input type="number" value={settings.auto_approve_escalation_days || 5}
                    onChange={(e) => setSettings({ ...settings, auto_approve_escalation_days: parseInt(e.target.value) || 5 })}
                    className="h-8 text-xs rounded-none w-24" />
                </div>
                <Button onClick={handleSaveSettings} disabled={saving}
                  className="h-8 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500 gap-1.5 mt-2">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Save Rules
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Bulk Grant */}
        <TabsContent value="bulk">
          <div className="bg-white border border-slate-200 p-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Bulk Leave Grant
            </h3>
            <div className="space-y-3 max-w-md">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Leave Type</label>
                <Select value={bulkType} onValueChange={setBulkType}>
                  <SelectTrigger className="h-8 text-xs rounded-none">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {leaveTypes.map((lt) => (
                      <SelectItem key={lt.id} value={lt.id} className="text-xs">{lt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Days to Grant</label>
                <Input type="number" value={bulkDays} onChange={(e) => setBulkDays(e.target.value)}
                  className="h-8 text-xs rounded-none" placeholder="e.g., 25" />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Department (optional)</label>
                <Input value={bulkDept} onChange={(e) => setBulkDept(e.target.value)}
                  className="h-8 text-xs rounded-none" placeholder="Leave blank for all employees" />
              </div>
              <Button onClick={handleBulkGrant}
                className="h-8 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500 gap-1.5">
                <CheckCircle2 className="w-3 h-3" />
                Execute Grant
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
